import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

interface Actualite {
  id: string;
  titre: string;
  description: string;
  contenu: string;
  type: string;
  image_url?: string;
  date_debut?: string;
  date_fin?: string;
  lieu?: string;
  auteur_id?: string;
  epingle: boolean;
  actif: boolean;
  date_creation: string;
  vues: number;
}

export default function AdminActualitesPage() {
  const { user, role, userRoles } = useAuth();
  const navigate = useNavigate();
  const isTrueSuperAdmin = userRoles?.includes('super_admin') ?? false;
  const [actualites, setActualites] = useState<Actualite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingActualite, setEditingActualite] = useState<Actualite | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [superAdminId, setSuperAdminId] = useState<string | null>(null);
  const [featuredId, setFeaturedId] = useState<string | null>(null);
  const [animationEnd, setAnimationEnd] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [configMessage, setConfigMessage] = useState('');
  const [configError, setConfigError] = useState('');

  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    contenu: '',
    type: 'information',
    image_url: '',
    date_debut: '',
    date_fin: '',
    lieu: '',
    epingle: false,
    actif: true
  });

  useEffect(() => {
    if (!user || (role !== 'super_admin' && role !== 'admin')) {
      navigate('/', { replace: true });
      return;
    }
    loadSuperAdminId();
    loadActualites();
    loadConfig();
  }, [user, role]);

  const loadSuperAdminId = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('super_admins')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Erreur chargement super_admin id:', error);
      } else if (data) {
        setSuperAdminId(data.id);
        console.log('[Admin] Super admin ID chargé:', data.id);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du super_admin id:', error);
    }
  };

  const loadActualites = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('actualites')
        .select('*')
        .order('epingle', { ascending: false })
        .order('date_creation', { ascending: false });

      if (error) {
        console.error('Erreur chargement actualités:', error);
      } else {
        setActualites(data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des actualités:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    // 🔧 Ne PAS appeler refreshSession() manuellement : cela consomme le refresh
    // token (à usage unique) et entre en conflit avec l'auto-refresh du SDK, ce
    // qui déclenche des 429 (rate limit) et des "session expirée" erronés.
    // getSession() renvoie le token déjà entretenu par le SDK.
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  };

  const loadConfig = async () => {
    try {
      const { data } = await supabase
        .from('system_config')
        .select('config_value')
        .eq('config_key', 'actualites_config')
        .maybeSingle();

      if (data?.config_value) {
        setFeaturedId(data.config_value.featured_id || null);
        const end = data.config_value.animation_end;
        if (end) {
          const d = new Date(end);
          const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
          setAnimationEnd(local);
        }
      }
    } catch (error) {
      console.error('Erreur chargement config actualités:', error);
    }
  };

  const saveConfig = async (nextFeaturedId: string | null, nextAnimationEnd: string) => {
    setSavingConfig(true);
    setConfigMessage('');
    setConfigError('');
    try {
      const token = await getAccessToken();
      if (!token) {
        setConfigError('Votre session a expiré. Vous allez être redirigé vers la connexion.');
        setTimeout(() => navigate('/login', { replace: true }), 1200);
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-system-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          configType: 'actualites_config',
          configData: {
            featured_id: nextFeaturedId,
            animation_end: nextAnimationEnd ? new Date(nextAnimationEnd).toISOString() : null,
          },
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de l\'enregistrement');
      }

      setConfigMessage('Paramètres des actualités enregistrés avec succès !');
      setTimeout(() => setConfigMessage(''), 3000);
    } catch (error: any) {
      setConfigError(error.message || 'Impossible d\'enregistrer');
      setTimeout(() => setConfigError(''), 3000);
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleFeatured = async (id: string) => {
    const nextId = featuredId === id ? null : id;
    setFeaturedId(nextId);
    await saveConfig(nextId, animationEnd);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Type de fichier non autorisé. Utilisez JPG, PNG, WebP ou GIF.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Le fichier est trop volumineux. Taille maximale : 5MB.');
      return;
    }

    setUploadingImage(true);
    setUploadError('');

    try {
      // Vérifier que la session est toujours active avant l'upload
      const { data: sessionCheck } = await supabase.auth.getSession();
      if (!sessionCheck?.session) {
        setUploadError('Votre session a expiré. Reconnectez-vous puis réessayez.');
        return;
      }

      const fileExtension = file.name.split('.').pop();
      const fileName = `actualite_${Date.now()}.${fileExtension}`;
      const filePath = fileName;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('actualites')
        .upload(filePath, file, {
          contentType: file.type,
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Erreur upload:', uploadError);
        const isAuthError = String(uploadError.message || '').toLowerCase().includes('security')
          || String(uploadError.message || '').toLowerCase().includes('jwt')
          || String(uploadError.message || '').toLowerCase().includes('token');
        setUploadError(
          isAuthError
            ? 'Votre session a expiré. Reconnectez-vous puis réessayez.'
            : 'Erreur lors du téléchargement de l\'image.',
        );
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('actualites')
        .getPublicUrl(filePath);

      setFormData({ ...formData, image_url: publicUrl });
    } catch (error) {
      console.error('Erreur upload:', error);
      setUploadError('Erreur lors du téléchargement');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!superAdminId) {
      alert('Erreur : Impossible de récupérer votre identifiant administrateur. Veuillez rafraîchir la page.');
      return;
    }

    const dataToSave = {
      titre: formData.titre,
      description: formData.description,
      contenu: formData.contenu,
      type: formData.type,
      image_url: formData.image_url || null,
      date_debut: formData.date_debut || null,
      date_fin: formData.date_fin || null,
      lieu: formData.lieu || null,
      auteur_id: superAdminId,
      epingle: formData.epingle,
      actif: formData.actif
    };

    console.log('[Admin] Données à sauvegarder:', dataToSave);

    try {
      if (editingActualite) {
        // Mise à jour
        const { error } = await supabase
          .from('actualites')
          .update(dataToSave)
          .eq('id', editingActualite.id);

        if (!error) {
          alert('Actualité modifiée avec succès !');
          resetForm();
          loadActualites();
        } else {
          console.error('Erreur modification:', error);
          alert(`Erreur lors de la modification : ${error.message}`);
        }
      } else {
        // Création
        const { error } = await supabase
          .from('actualites')
          .insert(dataToSave);

        if (!error) {
          alert('Actualité créée avec succès !');
          resetForm();
          loadActualites();
        } else {
          console.error('Erreur création:', error);
          alert(`Erreur lors de la création : ${error.message}`);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      alert('Une erreur est survenue lors de la soumission');
    }
  };

  const handleEdit = (actualite: Actualite) => {
    setEditingActualite(actualite);
    setFormData({
      titre: actualite.titre,
      description: actualite.description,
      contenu: actualite.contenu,
      type: actualite.type,
      image_url: actualite.image_url || '',
      date_debut: actualite.date_debut || '',
      date_fin: actualite.date_fin || '',
      lieu: actualite.lieu || '',
      epingle: actualite.epingle,
      actif: actualite.actif
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette actualité ?')) return;

    try {
      const { error } = await supabase
        .from('actualites')
        .delete()
        .eq('id', id);

      if (!error) {
        alert('Actualité supprimée avec succès !');
        loadActualites();
      } else {
        console.error('Erreur suppression:', error);
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Une erreur est survenue lors de la suppression');
    }
  };

  const toggleActif = async (actualite: Actualite) => {
    try {
      const { error } = await supabase
        .from('actualites')
        .update({ actif: !actualite.actif })
        .eq('id', actualite.id);

      if (!error) {
        loadActualites();
      } else {
        console.error('Erreur lors du changement de statut:', error);
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
    }
  };

  const toggleEpingle = async (actualite: Actualite) => {
    try {
      const { error } = await supabase
        .from('actualites')
        .update({ epingle: !actualite.epingle })
        .eq('id', actualite.id);

      if (!error) {
        loadActualites();
      } else {
        console.error('Erreur lors du changement de statut épinglé:', error);
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut épinglé:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      description: '',
      contenu: '',
      type: 'information',
      image_url: '',
      date_debut: '',
      date_fin: '',
      lieu: '',
      epingle: false,
      actif: true
    });
    setEditingActualite(null);
    setShowModal(false);
    setUploadError('');
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'don_sang': return 'bg-red-100 text-red-800';
      case 'evenement': return 'bg-purple-100 text-purple-800';
      case 'sante': return 'bg-green-100 text-green-800';
      case 'information': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'don_sang': return 'Don de sang';
      case 'evenement': return 'Événement';
      case 'sante': return 'Santé';
      case 'information': return 'Information';
      default: return type;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-clinic-blue-dark text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Actualités</h1>
              <p className="mt-2 text-gray-600">Créez et gérez les actualités de la clinique</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line text-xl"></i>
              Nouvelle actualité
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {isTrueSuperAdmin && (
            <div className="bg-white rounded-3xl shadow-xl border-2 border-clinic-blue/10 p-6 mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-clinic-blue-dark flex items-center gap-2">
                    <i className="ri-megaphone-line text-clinic-blue"></i>
                    Animation du bouton « Actualités »
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Choisissez jusqu'à quand le bouton « Actualités » reste animé (couleurs qui défilent + battement). Laissez vide pour une animation continue.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <input
                    type="datetime-local"
                    value={animationEnd}
                    onChange={(e) => setAnimationEnd(e.target.value)}
                    className="px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                  <button
                    onClick={() => saveConfig(featuredId, animationEnd)}
                    disabled={savingConfig}
                    className="px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {savingConfig ? (
                      <><i className="ri-loader-4-line animate-spin mr-2"></i>Enregistrement...</>
                    ) : (
                      <><i className="ri-save-line mr-2"></i>Enregistrer</>
                    )}
                  </button>
                </div>
              </div>
              {configMessage && (
                <p className="mt-3 text-sm text-green-600 flex items-center gap-1">
                  <i className="ri-check-line"></i>{configMessage}
                </p>
              )}
              {configError && (
                <p className="mt-3 text-sm text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>{configError}
                </p>
              )}
            </div>
          )}

          {actualites.length === 0 ? (
            <div className="bg-white p-12 rounded-3xl shadow-xl border-2 border-clinic-blue/10 text-center">
              <div className="w-20 h-20 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-newspaper-line text-4xl text-clinic-blue"></i>
              </div>
              <h3 className="text-2xl font-bold text-clinic-blue-dark mb-2">Aucune actualité</h3>
              <p className="text-gray-600 mb-6">Commencez par créer votre première actualité</p>
              <button
                onClick={() => setShowModal(true)}
                className="px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line mr-2"></i>
                Créer une actualité
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {actualites.map((actualite) => (
                <div
                  key={actualite.id}
                  className={`bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all ${
                    featuredId === actualite.id ? 'border-2 border-amber-400' : 'border-2 border-clinic-blue/10'
                  }`}
                >
                  {actualite.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={actualite.image_url}
                        alt={actualite.titre}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getTypeColor(actualite.type)}`}>
                        {getTypeText(actualite.type)}
                      </span>
                      <div className="flex items-center gap-2">
                        {featuredId === actualite.id && (
                          <i className="ri-star-fill text-amber-500" title="Mise en avant"></i>
                        )}
                        {actualite.epingle && (
                          <i className="ri-pushpin-fill text-orange-500" title="Épinglé"></i>
                        )}
                        {!actualite.actif && (
                          <i className="ri-eye-off-line text-gray-400" title="Désactivé"></i>
                        )}
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-clinic-blue-dark mb-2 line-clamp-2">
                      {actualite.titre}
                    </h3>

                    <p className="text-sm text-gray-600 mb-2 line-clamp-2 font-medium">
                      {actualite.description}
                    </p>

                    <p className="text-xs text-gray-500 mb-4 line-clamp-2">
                      {actualite.contenu}
                    </p>

                    {actualite.type === 'evenement' && (actualite.date_debut || actualite.lieu) && (
                      <div className="mb-4 p-3 bg-purple-50 rounded-xl border border-purple-100">
                        {actualite.date_debut && (
                          <div className="flex items-center gap-2 text-xs text-purple-700 mb-1">
                            <i className="ri-calendar-event-line"></i>
                            <span>
                              {formatDate(actualite.date_debut)}
                              {actualite.date_fin && ` - ${formatDate(actualite.date_fin)}`}
                            </span>
                          </div>
                        )}
                        {actualite.lieu && (
                          <div className="flex items-center gap-2 text-xs text-purple-700">
                            <i className="ri-map-pin-line"></i>
                            <span>{actualite.lieu}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                      <i className="ri-calendar-line"></i>
                      <span>{formatDate(actualite.date_creation)}</span>
                      <span className="mx-2">•</span>
                      <i className="ri-eye-line"></i>
                      <span>{actualite.vues} vues</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {isTrueSuperAdmin && (
                        <button
                          onClick={() => toggleFeatured(actualite.id)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                            featuredId === actualite.id
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={featuredId === actualite.id ? 'Retirer la mise en avant' : 'Mettre en avant dans le pop-up'}
                        >
                          <i className={featuredId === actualite.id ? 'ri-star-fill' : 'ri-star-line'}></i>
                        </button>
                      )}
                      <button
                        onClick={() => toggleActif(actualite)}
                        className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          actualite.actif
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title={actualite.actif ? 'Désactiver' : 'Activer'}
                      >
                        <i className={`${actualite.actif ? 'ri-eye-line' : 'ri-eye-off-line'} mr-1`}></i>
                        {actualite.actif ? 'Actif' : 'Inactif'}
                      </button>

                      <button
                        onClick={() => toggleEpingle(actualite)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          actualite.epingle
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title={actualite.epingle ? 'Désépingler' : 'Épingler'}
                      >
                        <i className={`${actualite.epingle ? 'ri-pushpin-fill' : 'ri-pushpin-line'}`}></i>
                      </button>

                      <button
                        onClick={() => handleEdit(actualite)}
                        className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-200 transition-all whitespace-nowrap cursor-pointer"
                        title="Modifier"
                      >
                        <i className="ri-edit-line"></i>
                      </button>

                      {isTrueSuperAdmin && (
                        <button
                          onClick={() => handleDelete(actualite.id)}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium hover:bg-red-200 transition-all whitespace-nowrap cursor-pointer"
                          title="Supprimer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal Création/Modification */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-3xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-clinic-blue-dark">
                {editingActualite ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
              </h3>
              <button
                onClick={resetForm}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Titre *
                </label>
                <input
                  type="text"
                  required
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                  placeholder="Titre de l'actualité..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Type *
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-3 pr-8 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                >
                  <option value="information">Information</option>
                  <option value="don_sang">Don de sang</option>
                  <option value="evenement">Événement</option>
                  <option value="sante">Santé</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Description courte *
                </label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  maxLength={200}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
                  placeholder="Résumé court de l'actualité (max 200 caractères)..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.description.length}/200 caractères</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Contenu complet *
                </label>
                <textarea
                  required
                  value={formData.contenu}
                  onChange={(e) => setFormData({ ...formData, contenu: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
                  placeholder="Contenu détaillé de l'actualité..."
                />
              </div>

              {formData.type === 'evenement' && (
                <div className="p-4 bg-purple-50 rounded-2xl border-2 border-purple-100 space-y-4">
                  <h4 className="font-semibold text-purple-900 flex items-center gap-2">
                    <i className="ri-calendar-event-line"></i>
                    Informations événement
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-purple-900 mb-2">
                        Date de début
                      </label>
                      <input
                        type="date"
                        value={formData.date_debut}
                        onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-purple-200 rounded-2xl focus:outline-none focus:border-purple-400 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-purple-900 mb-2">
                        Date de fin
                      </label>
                      <input
                        type="date"
                        value={formData.date_fin}
                        onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-purple-200 rounded-2xl focus:outline-none focus:border-purple-400 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-purple-900 mb-2">
                      Lieu
                    </label>
                    <input
                      type="text"
                      value={formData.lieu}
                      onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-2xl focus:outline-none focus:border-purple-400 text-sm"
                      placeholder="Adresse ou lieu de l'événement..."
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-clinic-blue-dark mb-2">
                  Image
                </label>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                      <div className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all ${
                        uploadingImage 
                          ? 'border-blue-400 bg-blue-50' 
                          : 'border-gray-300 hover:border-clinic-blue hover:bg-clinic-blue/5'
                      }`}>
                        {uploadingImage ? (
                          <div className="flex flex-col items-center gap-2">
                            <i className="ri-loader-4-line text-3xl text-blue-600 animate-spin"></i>
                            <p className="text-sm text-blue-600 font-medium">Téléchargement en cours...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <i className="ri-upload-cloud-2-line text-4xl text-clinic-blue"></i>
                            <p className="text-sm text-gray-700 font-medium">
                              Cliquez pour télécharger une image
                            </p>
                            <p className="text-xs text-gray-500">
                              JPG, PNG, WebP ou GIF (max 5MB)
                            </p>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {uploadError && (
                    <div className="p-3 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-2">
                      <i className="ri-error-warning-line text-red-600 text-lg mt-0.5"></i>
                      <p className="text-sm text-red-600">{uploadError}</p>
                    </div>
                  )}

                  {formData.image_url && (
                    <div className="relative">
                      <div className="border-2 border-green-200 rounded-2xl p-4 bg-green-50">
                        <div className="flex items-center gap-3 mb-3">
                          <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">Image téléchargée avec succès</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData({...formData, image_url: ''})}
                            className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-100 rounded-lg transition-colors cursor-pointer"
                            title="Supprimer l'image"
                          >
                            <i className="ri-delete-bin-line text-lg"></i>
                          </button>
                        </div>
                        <div>
                          <img 
                            src={formData.image_url} 
                            alt="Aperçu" 
                            className="w-full h-48 object-cover rounded-xl object-top"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.epingle}
                    onChange={(e) => setFormData({ ...formData, epingle: e.target.checked })}
                    className="w-5 h-5 text-clinic-blue rounded focus:ring-clinic-blue cursor-pointer"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    <i className="ri-pushpin-line mr-1"></i>
                    Épingler cette actualité
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.actif}
                    onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                    className="w-5 h-5 text-clinic-blue rounded focus:ring-clinic-blue cursor-pointer"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    <i className="ri-eye-line mr-1"></i>
                    Publier immédiatement
                  </span>
                </label>
              </div>

              <div className="flex gap-4 justify-end pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={uploadingImage}
                  className="px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-save-line mr-2"></i>
                  {editingActualite ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
